// ── Adapter Supabase do IEmpresaRepository ────────────────────────
// Wrapper em torno da tabela public.empresas + RPC public.buscar_empresas.
// Única camada que conhece a tabela/RPC. Núcleo depende só da porta.

import { getSupabase } from '@infra/supabase/client';
import type { Database } from '@/types/database.types';
import { normalizeEmpresaName } from '@modules/empresas';
import type {
  IEmpresaRepository,
  Empresa,
  EmpresaVisualStatus,
  EmpresaAuthor,
  EmpresaLeadStage,
  EmpresaLeadSummary,
  EmpresaHistoryEntry,
  EmpresaResponsible,
  EmpresaWithStats,
  CreateEmpresaInput,
  EmpresaUpdates,
  EmpresaLockHolder,
  EmpresaFilial,
  SalvarFilialInput,
} from '@modules/empresas';

const client = () => {
  const s = getSupabase();
  if (!s) throw new Error('Supabase nao inicializado.');
  return s;
};

const EMPRESA_COLS =
  'id, workspace_id, nome, cnpj, endereco, ativo, implantacao_liberada, created_by, updated_by, created_at, updated_at';

type EmpresaRow = {
  id: string;
  workspace_id: string;
  nome: string;
  cnpj: string | null;
  endereco: string | null;
  ativo: boolean | null;
  implantacao_liberada: boolean | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  is_exclusive?: boolean;
  visual_status?: EmpresaVisualStatus;
};

const mapEmpresa = (row: EmpresaRow): Empresa => ({
  id: row.id,
  workspace_id: row.workspace_id,
  nome: row.nome,
  cnpj: row.cnpj ?? null,
  endereco: row.endereco ?? null,
  ativo: row.ativo !== false,
  implantacaoLiberada: row.implantacao_liberada === true,
  created_by: row.created_by ?? null,
  updated_by: row.updated_by ?? null,
  created_at: row.created_at,
  updated_at: row.updated_at,
  isExclusive: row.is_exclusive,
  visualStatus: row.visual_status,
});

type VisualStatusRow = { empresa_id: string; status: EmpresaVisualStatus };

// Shapes do embed da página (leads + cards + profiles)
type AuthorRow = { id: string; nome: string | null; initials: string | null; color: string | null; avatar_url: string | null } | null;
type StatsRow = EmpresaRow & {
  workspace_name?: string;
  created_by_profile: AuthorRow;
  updated_by_profile: AuthorRow;
  leads: Array<{
    id: string;
    nome: string | null;
    workspace_id: string;
    workspace_name: string;
    contact_name: string | null;
    cargo: string | null;
    responsible_name: string | null;
    last_activity_at: string;
    presentation_at: string | null;
    stages: Array<{ role: string | null; stage: string | null }> | null;
  }> | null;
  history?: Array<{
    id: string;
    type: string;
    title: string;
    description: string | null;
    created_at: string;
    lead_name: string;
    workspace_name: string;
    author_name: string | null;
  }> | null;
};

type ResponsibleRow = {
  empresa_id: string;
  user_id: string;
  nome: string | null;
  initials: string | null;
  color: string | null;
  avatar_url: string | null;
};

const mapAuthor = (a: AuthorRow): EmpresaAuthor | null =>
  a ? { id: a.id, nome: a.nome, initials: a.initials, color: a.color, avatar_url: a.avatar_url } : null;

export class SupabaseEmpresaRepository implements IEmpresaRepository {
  listEmpresas = async (workspaceId: string): Promise<Empresa[]> => {
    const { data, error } = await client().rpc('buscar_empresas', {
      p_workspace_id: workspaceId,
      p_query: '',
      p_limit: 500,
    });
    if (error) throw error;
    return ((data || []) as unknown as EmpresaRow[]).map(mapEmpresa);
  };

  searchEmpresas = async (workspaceId: string, query: string, limit = 20): Promise<Empresa[]> => {
    const { data, error } = await client().rpc('buscar_empresas', {
      p_workspace_id: workspaceId,
      p_query: query,
      p_limit: limit,
    });
    if (error) throw error;
    const rows = (data || []) as unknown as EmpresaRow[];
    const statuses = new Map((await this.getVisualStatuses(rows.map(row => row.id))).map(item => [item.empresa_id, item.status]));
    return rows.map(row => {
      const status = statuses.get(row.id) || 'available';
      return mapEmpresa({ ...row, is_exclusive: status === 'unavailable', visual_status: status });
    });
  };

  private getVisualStatuses = async (empresaIds: string[]): Promise<VisualStatusRow[]> => {
    if (empresaIds.length === 0) return [];
    const { data, error } = await client().rpc('empresa_visual_statuses', {
      p_empresa_ids: [...new Set(empresaIds)],
    });
    if (error) {
      const missingRpc = error.code === 'PGRST202'
        || /empresa_visual_statuses.*schema cache|could not find.*empresa_visual_statuses/i.test(error.message || '');
      if (missingRpc) return [];
      throw error;
    }
    return (data || []) as VisualStatusRow[];
  };

  getLockHolders = async (empresaIds: string[]): Promise<EmpresaLockHolder[]> => {
    // ponytail: uma chamada paralela por empresa; trocar por RPC em lote se a busca passar de 20 itens.
    const holders = await Promise.all([...new Set(empresaIds)].map(async empresaId => {
      const { data, error } = await client().rpc('empresa_lock_holder', {
        p_empresa_id: empresaId,
        p_exclude_card_id: null,
        p_exclude_lead_id: null,
      } as never);
      if (error) throw error;
      return data ? { empresaId, holderLeadId: data as string } : null;
    }));
    return holders.filter((item): item is EmpresaLockHolder => item !== null);
  };

  getEmpresa = async (id: string): Promise<Empresa | null> => {
    const { data, error } = await client()
      .from('empresas')
      .select(EMPRESA_COLS)
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return data ? mapEmpresa(data as unknown as EmpresaRow) : null;
  };

  getEmpresaByLead = async (leadId: string): Promise<Empresa | null> => {
    // Duas queries em vez de um join embutido: `leads` tem RLS própria e o
    // embed devolveria null silenciosamente quando ela barrasse a empresa,
    // sem distinguir de "lead sem empresa".
    const { data: lead, error: errLead } = await client()
      .from('leads')
      .select('empresa_id')
      .eq('id', leadId)
      .maybeSingle();
    if (errLead) throw errLead;

    const empresaId = (lead as { empresa_id?: string | null } | null)?.empresa_id;
    if (!empresaId) return null;

    return this.getEmpresa(empresaId);
  };

  insertEmpresa = async (input: CreateEmpresaInput): Promise<Empresa> => {
    const payload = {
      workspace_id: input.workspaceId,
      nome: input.nome,
      cnpj: input.cnpj ?? null,
      endereco: input.endereco ?? null,
    };
    const { data, error } = await client()
      .from('empresas')
      // scope_key é preenchido pelo trigger trg_empresas_set_scope (o banco é autoritativo).
      // O gerador de tipos não enxerga triggers e marca a coluna como obrigatória no insert.
      .insert(payload as Database['public']['Tables']['empresas']['Insert'])
      .select(EMPRESA_COLS)
      .single();

    if (error) {
      // Índice único (nome normalizado ou cnpj): já existe → devolve a existente.
      if (error.code === '23505') {
        const existing = await this.findByNormalizedName(input.workspaceId, input.nome);
        if (existing) return existing;
      }
      throw error;
    }
    return mapEmpresa(data as unknown as EmpresaRow);
  };

  updateEmpresa = async (id: string, updates: EmpresaUpdates): Promise<Empresa> => {
    const { implantacaoLiberada, ...rest } = updates;
    const { data, error } = await client()
      .from('empresas')
      .update({ ...rest, ...(implantacaoLiberada === undefined ? {} : { implantacao_liberada: implantacaoLiberada }) })
      .eq('id', id)
      .select(EMPRESA_COLS)
      .single();
    if (error) throw error;
    return mapEmpresa(data as unknown as EmpresaRow);
  };

  deleteEmpresa = async (id: string): Promise<void> => {
    // leads.empresa_id tem ON DELETE SET NULL → os leads são desvinculados, não apagados.
    const { error } = await client().from('empresas').delete().eq('id', id);
    if (error) throw error;
  };

  listEmpresasWithStats = async (workspaceId: string): Promise<EmpresaWithStats[]> => {
    const [companiesResult, responsiblesResult] = await Promise.all([
      client().rpc('listar_empresas_compartilhadas', { p_workspace_id: workspaceId }),
      client().rpc('listar_responsaveis_empresas', { p_workspace_id: workspaceId }),
    ]);
    if (companiesResult.error || responsiblesResult.error) {
      throw companiesResult.error || responsiblesResult.error;
    }

    const companyRows = (companiesResult.data || []) as unknown as StatsRow[];
    const statuses = new Map((await this.getVisualStatuses(companyRows.map(row => row.id))).map(item => [item.empresa_id, item.status]));

    const responsiblesByEmpresa = new Map<string, EmpresaResponsible[]>();
    for (const row of (responsiblesResult.data || []) as ResponsibleRow[]) {
      const list = responsiblesByEmpresa.get(row.empresa_id) || [];
      list.push({ id: row.user_id, nome: row.nome, initials: row.initials, color: row.color, avatarUrl: row.avatar_url });
      responsiblesByEmpresa.set(row.empresa_id, list);
    }

    return companyRows.map((row) => {
      const leads: EmpresaLeadSummary[] = (row.leads || []).map(l => ({
        id: l.id,
        nome: l.nome || '',
        workspaceId: l.workspace_id,
        workspaceName: l.workspace_name,
        contactName: l.contact_name,
        cargo: l.cargo,
        responsibleName: l.responsible_name,
        lastActivityAt: l.last_activity_at,
        presentationAt: l.presentation_at,
        stages: l.stages || [],
      }));
      const stages: EmpresaLeadStage[] = [];
      for (const lead of leads) {
        for (const card of lead.stages) {
          stages.push({
            leadId: lead.id,
            leadNome: lead.nome,
            role: card.role,
            stage: card.stage,
          });
        }
      }
      const history: EmpresaHistoryEntry[] = (row.history || []).map(h => ({
        id: h.id,
        type: h.type,
        title: h.title,
        description: h.description,
        createdAt: h.created_at,
        leadName: h.lead_name,
        workspaceName: h.workspace_name,
        authorName: h.author_name,
      }));
      return {
        ...mapEmpresa({ ...row, visual_status: statuses.get(row.id) || 'available' }),
        workspaceName: row.workspace_name || '',
        leadsCount: leads.length,
        leads,
        responsibles: responsiblesByEmpresa.get(row.id) || [],
        history,
        lastActivityAt: leads.reduce<string | null>((latest, lead) =>
          !latest || lead.lastActivityAt > latest ? lead.lastActivityAt : latest, null),
        stages,
        createdByProfile: mapAuthor(row.created_by_profile),
        updatedByProfile: mapAuthor(row.updated_by_profile),
      };
    });
  };

  // Fallback para resolver a empresa existente quando o insert colide no índice único.
  private findByNormalizedName = async (workspaceId: string, nome: string): Promise<Empresa | null> => {
    const target = normalizeEmpresaName(nome);
    const candidates = await this.searchEmpresas(workspaceId, nome, 10);
    return candidates.find(e => normalizeEmpresaName(e.nome) === target) ?? null;
  };

  // ── Filiais ─────────────────────────────────────────────────────
  // workspace_id e scope_key são do trigger (copiados da empresa), e o CNPJ
  // chega já normalizado pelo serviço — o banco normaliza de novo.

  listFiliais = async (empresaId: string): Promise<EmpresaFilial[]> => {
    const { data, error } = await filiais()
      .select(FILIAL_COLS).eq('empresa_id', empresaId).eq('ativo', true)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return ((data || []) as FilialRow[]).map(mapFilial);
  };

  criarFilial = async (empresaId: string, input: SalvarFilialInput): Promise<EmpresaFilial> => {
    const { data, error } = await filiais()
      // Sem workspace_id/scope_key: o NOT NULL é conferido depois do trigger
      // BEFORE, que os copia da empresa.
      .insert({ empresa_id: empresaId, ...filialPayload(input) })
      .select(FILIAL_COLS).single();
    if (error) throw erroDeFilial(error);
    return mapFilial(data as FilialRow);
  };

  atualizarFilial = async (filialId: string, input: SalvarFilialInput): Promise<EmpresaFilial> => {
    const { data, error } = await filiais()
      .update(filialPayload(input)).eq('id', filialId)
      .select(FILIAL_COLS).single();
    if (error) throw erroDeFilial(error);
    return mapFilial(data as FilialRow);
  };

  removerFilial = async (filialId: string): Promise<void> => {
    const { error } = await filiais().update({ ativo: false }).eq('id', filialId);
    if (error) throw error;
  };
}

// public.empresa_filiais ainda não está no `Database` gerado (regerar os
// tipos depois de aplicar 20260930140000). O cast fica isolado aqui.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const filiais = (): any => (client() as unknown as { from(t: string): unknown }).from('empresa_filiais');

// Os 23505 do trigger já vêm com frase ("é o da matriz", "já está cadastrado
// como a empresa X"). O do ÍNDICE único não: é o PostgREST dizendo
// "duplicate key value violates ux_empresa_filiais_scope_cnpj" — que também é
// o que um reenvio depois de timeout recebe, quando a filial já entrou.
const erroDeFilial = (error: { code?: string; message?: string }) =>
  error.code === '23505' && /ux_empresa_filiais_scope_cnpj/.test(error.message || '')
    ? new Error('Este CNPJ já é filial de uma empresa. Se você acabou de adicioná-lo, recarregue a lista.')
    : error;

const FILIAL_COLS =
  'id, empresa_id, cnpj, razao_social, nome_fantasia, email_cobranca, dados_proprios, endereco, email, representante_nome, representante_cpf, email_assinatura';

type FilialRow = {
  id: string;
  empresa_id: string;
  cnpj: string;
  razao_social: string;
  nome_fantasia: string | null;
  email_cobranca: string | null;
  dados_proprios: boolean | null;
  endereco: string | null;
  email: string | null;
  representante_nome: string | null;
  representante_cpf: string | null;
  email_assinatura: string | null;
};

const mapFilial = (row: FilialRow): EmpresaFilial => ({
  id: row.id,
  empresaId: row.empresa_id,
  cnpj: row.cnpj,
  razaoSocial: row.razao_social,
  nomeFantasia: row.nome_fantasia ?? '',
  emailCobranca: row.email_cobranca ?? '',
  dadosProprios: row.dados_proprios === true,
  endereco: row.endereco ?? '',
  email: row.email ?? '',
  representanteNome: row.representante_nome ?? '',
  representanteCpf: row.representante_cpf ?? '',
  emailAssinatura: row.email_assinatura ?? '',
});

const filialPayload = (input: SalvarFilialInput) => ({
  cnpj: input.cnpj,
  razao_social: input.razaoSocial,
  nome_fantasia: input.nomeFantasia || null,
  email_cobranca: input.emailCobranca || null,
  dados_proprios: input.dadosProprios,
  endereco: input.endereco || null,
  email: input.email || null,
  representante_nome: input.representanteNome || null,
  representante_cpf: input.representanteCpf || null,
  email_assinatura: input.emailAssinatura || null,
});
